import { Field, Form } from "react-final-form";
import { Input, RichTextField } from "../../components/inputs";
import {
  IoCloudUploadOutline,
  IoDocumentTextOutline,
  IoGlobeOutline,
  IoImageOutline,
} from "react-icons/io5";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../components/button";
import { shadow } from "../../assets/styles/parameters";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

export const ServicesForm = ({ initialValues, onFormSubmit }) => {
  const navigate = useNavigate();
  const [newFiles, setNewFiles] = useState([]);
  const [localMediaPreview, setLocalMediaPreview] = useState(null);

  useEffect(() => {
    if (newFiles.length > 0) {
      const objectUrl = URL.createObjectURL(newFiles[0]);
      setLocalMediaPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setLocalMediaPreview(initialValues?.media?.[0]?.url || null);
    }
  }, [newFiles, initialValues]);

  const handleFileChange = (e) => {
    setNewFiles(Array.from(e.target.files));
  };

  const handleFormSubmit = (values) => {
    onFormSubmit(values, newFiles);
  };

  const videoRef = useRef(null);

  // Dynamically derive media type based on the new schema structure
  const getMediaType = () => {
    let isVideo = false;
    let isImage = false;

    if (newFiles.length > 0) {
      const fileType = newFiles[0].type;
      if (fileType.startsWith("video/")) isVideo = true;
      else if (fileType.startsWith("image/")) isImage = true;
    } else if (initialValues?.media?.length > 0) {
      const cType = initialValues.media[0].contentType || "";
      if (cType.startsWith("video/")) isVideo = true;
      else if (cType.startsWith("image/")) isImage = true;
    }
    return { isVideo, isImage };
  };

  const renderMedia = () => {
    if (!localMediaPreview) {
      return (
        <EmptyViewer>
          <IoImageOutline size={48} color="#D1D5DB" />
          <p>No media currently attached.</p>
        </EmptyViewer>
      );
    }

    const { isVideo, isImage } = getMediaType();

    if (isImage) {
      return <MediaImage src={localMediaPreview} alt="Preview" />;
    } else if (isVideo) {
      return (
        <MediaVideo
          controls
          ref={videoRef}
          key={localMediaPreview}
          src={localMediaPreview}
        />
      );
    } else {
      return (
        <FilePreviewWrapper>
          <FileCard
            href={localMediaPreview}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IoDocumentTextOutline size={24} color="#3B5BDB" />
            <span>{newFiles[0]?.name || "View Attached Document"}</span>
          </FileCard>
        </FilePreviewWrapper>
      );
    }
  };

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };
  }, [localMediaPreview]);

  return (
    <Form
      onSubmit={handleFormSubmit}
      initialValues={initialValues || {}}
      render={({ handleSubmit, submitting, pristine, values }) => (
        <form onSubmit={handleSubmit}>
          <WorkspaceGrid>
            {/* LEFT PANE: The Form */}
            <WidgetCard>
              <SectionTitle>Service Configuration</SectionTitle>
              <SectionSubtitle>
                Update the public-facing details.
              </SectionSubtitle>

              <Field
                name="title"
                type="text"
                component={Input}
                label="Service Title"
                entity="&#10029;"
              />

              <Field
                name="description"
                component={RichTextField}
                label="Service Description"
                entity="&#10029;"
              />

              <UploadGroup>
                <UploadLabel>Attach Media File</UploadLabel>
                <UploadZone>
                  <IoCloudUploadOutline size={32} color="#3B5BDB" />
                  <UploadText>
                    <strong>Click to upload</strong> or drag and drop
                  </UploadText>
                  <UploadHint>PNG, JPG, MP4 or PDF (max. 10MB)</UploadHint>
                  {newFiles.length > 0 && (
                    <SelectedFileText>
                      Selected: {newFiles[0].name}
                    </SelectedFileText>
                  )}
                  <FileInput type="file" onChange={handleFileChange} />
                </UploadZone>
              </UploadGroup>

              <ButtonFlex>
                <CancelText onClick={() => navigate(-1)}>Cancel</CancelText>
                <Button
                  type="submit"
                  disabled={submitting || (pristine && newFiles.length === 0)}
                  text={submitting ? "Saving..." : "Save Service"}
                />
              </ButtonFlex>
            </WidgetCard>

            {/* RIGHT PANE: Live Preview */}
            <WidgetCard bg="#F9FAFB">
              <SectionTitle>Live Web Preview</SectionTitle>
              <SectionSubtitle>How this appears to patients.</SectionSubtitle>

              <BrowserFrame>
                <BrowserTopBar>
                  <BrowserDots>
                    <span style={{ background: "#FF5F56" }} />
                    <span style={{ background: "#FFBD2E" }} />
                    <span style={{ background: "#27C93F" }} />
                  </BrowserDots>
                  <BrowserUrlBar>
                    <IoGlobeOutline size={14} color="#9CA3AF" />
                    <span>oasishhcare.com/services/</span>
                    <strong>
                      {(values.title || "new-service")
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, "")}
                    </strong>
                  </BrowserUrlBar>
                </BrowserTopBar>

                <BrowserBody>
                  <PreviewMediaBox>
                    {renderMedia()}
                    <MediaTypeBadge bg="#111827" color="white">
                      {getMediaType().isVideo
                        ? "VIDEO"
                        : getMediaType().isImage
                          ? "IMAGE"
                          : localMediaPreview
                            ? "FILE"
                            : "TEXT ONLY"}
                    </MediaTypeBadge>
                  </PreviewMediaBox>

                  <PreviewContent>
                    <PreviewTitle>
                      {values.title || "Service Title"}
                    </PreviewTitle>
                    <PreviewDescription
                      dangerouslySetInnerHTML={{
                        __html:
                          values.description ||
                          "<p>Your description will appear here...</p>",
                      }}
                    />
                  </PreviewContent>
                </BrowserBody>
              </BrowserFrame>
            </WidgetCard>
          </WorkspaceGrid>
        </form>
      )}
    />
  );
};

// --- STYLED COMPONENTS ---

const WorkspaceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const WidgetCard = styled.div`
  background: ${(props) => props.bg || "white"};
  padding: 32px;
  border-radius: 16px;
  box-shadow: ${shadow.light};
  border: 1px solid rgba(0, 0, 0, 0.02);
  /* Prevent card from exceeding its grid cell width */
  min-width: 0;
  overflow: hidden;

  @media screen and (max-width: 640px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 20px;
  color: #111827;
  font-weight: 700;
`;
const SectionSubtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 24px 0;
`;

const UploadGroup = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const UploadLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #374151;
`;
const UploadZone = styled.div`
  position: relative;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  &:hover {
    border-color: #3b5bdb;
    background: #e6f2ff;
  }
`;
const UploadText = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  strong {
    color: #3b5bdb;
  }
`;
const UploadHint = styled.span`
  color: #9ca3af;
  font-size: 12px;
`;
const SelectedFileText = styled.div`
  margin-top: 8px;
  padding: 4px 12px;
  background: #e8f8f5;
  color: #2eca7f;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
`;
const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const ButtonFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;
const CancelText = styled.span`
  cursor: pointer;
  color: #6c757d;
  font-weight: 600;
  transition: all 0.2s;
  &:hover {
    color: #dc3545;
    text-decoration: underline;
  }
`;

const BrowserFrame = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;
const BrowserTopBar = styled.div`
  background: #f3f4f6;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  gap: 16px;
`;
const BrowserDots = styled.div`
  display: flex;
  gap: 6px;
  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
`;
const BrowserUrlBar = styled.div`
  background: white;
  padding: 6px 16px;
  border-radius: 6px;
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  strong {
    color: #111;
    font-weight: 500;
  }
`;
const BrowserBody = styled.div`
  display: flex;
  flex-direction: column;
`;
const PreviewMediaBox = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f9fafb;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid #f3f4f6;
`;
const MediaTypeBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;
const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const MediaVideo = styled.video`
  width: 100%;
  height: 100%;
  background: black;
`;
const FilePreviewWrapper = styled.div`
  padding: 24px;
  width: 100%;
  display: flex;
  justify-content: center;
`;
const EmptyViewer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #9ca3af;
  p {
    margin-top: 12px;
    font-size: 14px;
  }
`;
const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 16px 24px;
  border-radius: 8px;
  text-decoration: none;
  color: #111;
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05);
    border-color: #3b5bdb;
  }
`;
const PreviewContent = styled.div`
  padding: 32px 40px;

  @media screen and (max-width: 640px) {
    padding: 1.25rem;
  }
`;
const PreviewTitle = styled.h1`
  margin: 0 0 20px 0;
  font-size: 28px;
  color: #111827;
  font-weight: 800;
  letter-spacing: -0.5px;
`;
const PreviewDescription = styled.div`
  color: #4b5563;
  line-height: 1.7;
  font-size: 16px;

  h1,
  h2,
  h3 {
    color: #111827;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: 700;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }

  p {
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 24px;
    margin-bottom: 16px;
  }
  li {
    margin-bottom: 8px;
  }

  blockquote {
    border-left: 4px solid #3b5bdb;
    margin: 0 0 16px 0;
    padding-left: 16px;
    font-style: italic;
    color: #6b7280;
    background: #f9fafb;
    padding: 12px 16px;
    border-radius: 0 8px 8px 0;
  }

  a {
    color: #3b5bdb;
    text-decoration: none;
    font-weight: 500;
  }
  a:hover {
    text-decoration: underline;
  }
`;
